import { KuikaWidgetRegistry, WidgetPackManifest } from "../components/web-components/widget-renderer/types";
import { KuikaAppManager } from "../shared/utilty/kuika-app-manager";

/**
 * MFE app manifest structure.
 */
export interface MfeManifest {
  webComponentTag: string;
  /**
   * Authoritative backend URL for the microapp, written by codegen at deploy
   * time. The host's MFE container reads this directly during auth handoff so
   * it never has to guess the API host from the frontend URL — works for any
   * topology (Kuika SaaS, self-hosted single-tenant, custom domains, etc.).
   * Optional for backward compatibility with manifests generated before this
   * field was introduced.
   */
  backendUrlOverride?: string;
  entryFiles: {
    /** Legacy support */
    app?: {
      js: string[];
      css: string[];
      ico?: string[];
      url_3rd_js?: string[];
    };
    /** New support */
    index?: {
      js: string[];
      css: string[];
      ico?: string[];
      url_3rd_js?: string[];
    };
  };
}

/**
 * Static service for loading widget pack and MFE assets (JS, CSS, manifests).
 * Handles caching, deduplication of requests, and proper loading order.
 */
export class AssetLoaderService {
  private static readonly DESIGN_MODE_ORIGIN = "http://localhost:3002";

  /** Cache for widget manifests to prevent redundant network requests */
  private static readonly manifestCache = new Map<string, WidgetPackManifest>();

  /** Cache for in-flight manifest fetch promises to prevent duplicate concurrent requests */
  private static readonly manifestFetchPromises = new Map<string, Promise<WidgetPackManifest>>();

  /** Cache for in-flight component loading promises to prevent duplicate concurrent loads */
  private static readonly loadingPromises = new Map<string, Promise<WidgetPackManifest>>();

  /** Set of already loaded widget components */
  private static readonly loadedComponents = new Set<string>();

  /** Cache for MFE manifests */
  private static readonly mfeManifestCache = new Map<string, MfeManifest>();

  /** Cache for in-flight MFE manifest fetch promises */
  private static readonly mfeFetchPromises = new Map<string, Promise<MfeManifest>>();

  /** Set of already loaded MFE apps */
  private static readonly loadedMfeApps = new Set<string>();

  /**
   * Returns the origin to use for loading widget assets.
   * In design time, uses localhost:3002; otherwise uses GetFrontEndSourceUrl for proper MFE/web component support.
   */
  private static getWidgetOrigin(): string {
    if (window.kuika?.isDesignTime) {
      return this.DESIGN_MODE_ORIGIN;
    }
    return KuikaAppManager.GetFrontEndSourceUrl();
  }

  /**
   * Gets the entry files from a manifest for a specific component.
   * Falls back to 'index' or 'app' for legacy single-bundle packs.
   */
  private static getEntryFiles(manifest: WidgetPackManifest, componentId?: string) {
    if (componentId && manifest.entryFiles?.[componentId]) {
      return manifest.entryFiles[componentId];
    }
    return manifest.entryFiles?.index ?? manifest.entryFiles?.app;
  }

  /**
   * Returns the base path for a widget pack's assets.
   */
  public static getWidgetPackBasePath(packId: string): string {
    const origin = this.getWidgetOrigin();
    return `${origin}/widgets/${packId}/`;
  }

  /**
   * Fetches and parses the asset-manifest.json for a widget pack.
   * Uses caching to prevent redundant network requests.
   */
  public static async fetchWidgetPackManifest(packId: string): Promise<WidgetPackManifest> {
    const cached = this.manifestCache.get(packId);
    if (cached) {
      return cached;
    }

    const existingFetch = this.manifestFetchPromises.get(packId);
    if (existingFetch) {
      return existingFetch;
    }

    const fetchPromise = (async () => {
      const basePath = this.getWidgetPackBasePath(packId);
      const manifestUrl = `${basePath}asset-manifest.json`;

      const response = await fetch(manifestUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch widget manifest: ${response.status} ${response.statusText}`);
      }

      const manifest: WidgetPackManifest = await response.json();
      this.manifestCache.set(packId, manifest);

      return manifest;
    })();

    this.manifestFetchPromises.set(packId, fetchPromise);

    try {
      return await fetchPromise;
    } finally {
      this.manifestFetchPromises.delete(packId);
    }
  }

  /**
   * Creates a script element and waits for it to load.
   * @param src - The script URL
   * @param isModule - If true, loads the script as an ES module (type="module")
   */
  public static loadScript(src: string, isModule: boolean = false): Promise<void> {
    return new Promise((resolve, reject) => {
      const existingScript = document.querySelector(`script[src="${src}"]`);
      if (existingScript) {
        resolve();
        return;
      }

      const script = document.createElement("script");
      script.async = true;
      script.crossOrigin = "anonymous";
      script.referrerPolicy = "no-referrer";
      script.src = src;

      if (isModule) {
        script.type = "module";
      }

      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${src}`));

      document.head.appendChild(script);
    });
  }

  /**
   * Creates a link element for CSS and waits for it to load.
   */
  public static loadStylesheet(href: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const existingLink = document.querySelector(`link[href="${href}"]`);
      if (existingLink) {
        resolve();
        return;
      }

      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;

      link.onload = () => resolve();
      link.onerror = () => reject(new Error(`Failed to load stylesheet: ${href}`));

      document.head.appendChild(link);
    });
  }

  /**
   * Loads all JS and CSS assets for a widget pack or specific component.
   */
  public static async loadWidgetPackAssets(
    packId: string,
    manifest: WidgetPackManifest,
    componentId?: string
  ): Promise<void> {
    const basePath = this.getWidgetPackBasePath(packId);
    const entryFiles = this.getEntryFiles(manifest, componentId);

    if (!entryFiles) {
      throw new Error(`No entry files found in widget manifest for ${packId}${componentId ? `/${componentId}` : ""}`);
    }

    const isModule = manifest.scriptType === "module";

    const cssPromises = (entryFiles.css || []).map((file) => this.loadStylesheet(`${basePath}${file}`));

    for (const file of entryFiles.js || []) {
      await this.loadScript(`${basePath}${file}`, isModule);
    }

    await Promise.all(cssPromises);
  }

  /**
   * Checks if a widget component has already been loaded.
   */
  public static isWidgetComponentLoaded(packId: string, componentId?: string): boolean {
    const key = componentId ? `${packId}/${componentId}` : packId;
    return this.loadedComponents.has(key);
  }

  /**
   * Marks a widget component as loaded.
   */
  public static markWidgetComponentLoaded(packId: string, componentId?: string): void {
    const key = componentId ? `${packId}/${componentId}` : packId;
    this.loadedComponents.add(key);
  }

  /**
   * Waits for the widget registry to become available after scripts load.
   */
  public static waitForWidgetRegistry(packId: string, timeout: number = 10000): Promise<KuikaWidgetRegistry> {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();

      const check = () => {
        const registry = window.kuikaWidgetRegistry?.[packId];
        if (registry) {
          resolve(registry);
          return;
        }

        if (Date.now() - startTime > timeout) {
          reject(new Error(`Timeout waiting for widget registry: ${packId}`));
          return;
        }

        setTimeout(check, 50);
      };

      check();
    });
  }

  /**
   * Loads a specific widget component from a pack.
   * Only loads the assets needed for that component.
   * Handles caching and prevents duplicate loading.
   */
  public static async loadWidgetComponent(packId: string, componentId: string): Promise<WidgetPackManifest> {
    const cacheKey = `${packId}/${componentId}`;

    if (this.isWidgetComponentLoaded(packId, componentId)) {
      return this.fetchWidgetPackManifest(packId);
    }

    const existingPromise = this.loadingPromises.get(cacheKey);
    if (existingPromise) {
      return existingPromise;
    }

    const loadingPromise = (async () => {
      try {
        const manifest = await this.fetchWidgetPackManifest(packId);
        await this.loadWidgetPackAssets(packId, manifest, componentId);
        await this.waitForWidgetRegistry(packId);
        this.markWidgetComponentLoaded(packId, componentId);
        return manifest;
      } finally {
        this.loadingPromises.delete(cacheKey);
      }
    })();

    this.loadingPromises.set(cacheKey, loadingPromise);
    return loadingPromise;
  }

  // ==================== MFE Loading Methods ====================

  /**
   * Normalizes a frontend URL to ensure it ends with a trailing slash.
   */
  public static normalizeFrontEndUrl(url: string): string {
    if (!url) return url;
    let normalized = url;
    if (normalized.endsWith("/") || normalized.endsWith("\\")) {
      normalized = normalized.slice(0, -1);
    }
    return `${normalized}/`;
  }

  /**
   * Gets the entry files from an MFE manifest.
   * Falls back to 'app' for legacy manifests.
   */
  private static getMfeEntryFiles(manifest: MfeManifest) {
    return manifest?.entryFiles?.index ?? manifest?.entryFiles?.app;
  }

  /**
   * Fetches and caches an MFE manifest from the given frontend URL.
   */
  public static async fetchMfeManifest(frontEndUrl: string): Promise<MfeManifest> {
    const normalizedUrl = this.normalizeFrontEndUrl(frontEndUrl);

    const cached = this.mfeManifestCache.get(normalizedUrl);
    if (cached) {
      return cached;
    }

    const existingFetch = this.mfeFetchPromises.get(normalizedUrl);
    if (existingFetch) {
      return existingFetch;
    }

    const fetchPromise = (async () => {
      const manifestUrl = `${normalizedUrl}asset-manifest.json`;
      const response = await fetch(manifestUrl);

      if (!response.ok) {
        throw new Error(`Failed to fetch MFE manifest: ${response.status} ${response.statusText}`);
      }

      const manifest: MfeManifest = await response.json();
      this.mfeManifestCache.set(normalizedUrl, manifest);

      return manifest;
    })();

    this.mfeFetchPromises.set(normalizedUrl, fetchPromise);

    try {
      return await fetchPromise;
    } finally {
      this.mfeFetchPromises.delete(normalizedUrl);
    }
  }

  /**
   * Loads a link element (for icons, apple-touch-icon, etc.).
   */
  public static loadLink(href: string, rel: string, type?: string): Promise<void> {
    return new Promise((resolve) => {
      const existingLink = document.querySelector(`link[href="${href}"][rel="${rel}"]`);
      if (existingLink) {
        resolve();
        return;
      }

      const link = document.createElement("link");
      link.rel = rel;
      link.href = href;
      if (type) {
        link.type = type;
      }

      link.onload = () => resolve();
      link.onerror = () => resolve(); // Don't fail on icon load errors

      document.head.appendChild(link);
    });
  }

  /**
   * Loads all assets for an MFE app from a manifest.
   */
  public static async loadMfeAssets(
    frontEndUrl: string,
    manifest: MfeManifest,
    isModule: boolean = false,
    loadCss: boolean = true
  ): Promise<void> {
    const normalizedUrl = this.normalizeFrontEndUrl(frontEndUrl);
    const entryFiles = this.getMfeEntryFiles(manifest);

    if (!entryFiles) {
      throw new Error(`No entry files found in MFE manifest for ${frontEndUrl}`);
    }

    // Load CSS files (non-blocking). Skipped when loadCss is false — e.g. a
    // shadow-DOM-mounted MFE loads its stylesheets INTO its shadow root instead
    // (see getMfeStylesheetUrls + loadStylesheetIntoRoot), so the MFE's CSS never
    // leaks into the host document and isolation is preserved.
    const cssPromises = loadCss
      ? (entryFiles.css || []).map((file) => this.loadStylesheet(`${normalizedUrl}${file}`))
      : [];

    // Load JS files in order (they may depend on each other). isModule loads the
    // entry bundle as <script type="module"> — required for ESM bundles (e.g. a
    // Vite-built MFE that uses import.meta), which throw "import.meta may only
    // appear in a module" when injected as a classic script. JS always loads into
    // document.head: a custom-element definition is document-global and upgrades
    // instances inside any shadow tree.
    for (const file of entryFiles.js || []) {
      await this.loadScript(`${normalizedUrl}${file}`, isModule);
    }

    // Load 3rd party JS URLs
    for (const url of entryFiles.url_3rd_js || []) {
      await this.loadScript(url);
    }

    // Load icons (fire-and-forget — icon link elements like apple-touch-icon
    // may never fire onload/onerror in non-Safari browsers, so we must not await them)
    (entryFiles.ico || []).forEach((file) => {
      this.loadLink(`${normalizedUrl}${file}`, "icon", "image/png");
      this.loadLink(`${normalizedUrl}${file}`, "apple-touch-icon");
    });

    // Wait for CSS only
    await Promise.all(cssPromises);
  }

  /**
   * Checks if an MFE app has already been loaded.
   */
  public static isMfeAppLoaded(frontEndUrl: string): boolean {
    const normalizedUrl = this.normalizeFrontEndUrl(frontEndUrl);
    return this.loadedMfeApps.has(normalizedUrl);
  }

  /**
   * Marks an MFE app as loaded.
   */
  public static markMfeAppLoaded(frontEndUrl: string): void {
    const normalizedUrl = this.normalizeFrontEndUrl(frontEndUrl);
    this.loadedMfeApps.add(normalizedUrl);
  }

  /**
   * Loads an MFE app from a frontend URL.
   * Handles manifest fetching, asset loading, and caching.
   * Returns the manifest if successful.
   */
  public static async loadMfeApp(
    frontEndUrl: string,
    isModule: boolean = false,
    loadCss: boolean = true
  ): Promise<MfeManifest> {
    const normalizedUrl = this.normalizeFrontEndUrl(frontEndUrl);

    // If already loaded, just return cached manifest
    if (this.isMfeAppLoaded(normalizedUrl)) {
      return this.fetchMfeManifest(normalizedUrl);
    }

    const manifest = await this.fetchMfeManifest(normalizedUrl);

    if (!manifest.webComponentTag) {
      throw new Error(`MFE manifest missing webComponentTag for ${frontEndUrl}`);
    }

    await this.loadMfeAssets(normalizedUrl, manifest, isModule, loadCss);
    this.markMfeAppLoaded(normalizedUrl);

    return manifest;
  }

  /**
   * Returns the absolute URLs of an MFE manifest's stylesheets. Use this to load
   * an MFE's CSS into a shadow root (with loadStylesheetIntoRoot) when the MFE is
   * mounted in shadow DOM and its assets were loaded with loadCss=false.
   */
  public static getMfeStylesheetUrls(frontEndUrl: string, manifest: MfeManifest): string[] {
    const normalizedUrl = this.normalizeFrontEndUrl(frontEndUrl);
    const entryFiles = this.getMfeEntryFiles(manifest);
    return (entryFiles?.css || []).map((file) => `${normalizedUrl}${file}`);
  }

  /**
   * Loads a stylesheet into a shadow root (not document.head) so it applies only
   * within that shadow tree. Deduplicated per root.
   */
  public static loadStylesheetIntoRoot(href: string, root: ShadowRoot): Promise<void> {
    return new Promise((resolve, reject) => {
      const existingLink = root.querySelector(`link[href="${href}"]`);
      if (existingLink) {
        resolve();
        return;
      }

      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;

      link.onload = () => resolve();
      link.onerror = () => reject(new Error(`Failed to load stylesheet: ${href}`));

      root.appendChild(link);
    });
  }
}
